// Falcon CRM (Phase 1, approved 21/10/2026): customers and leads, contacts, deals on a pipeline board, calls and notes on
// one timeline, tasks and follow-ups, and the Today screen. Sold on its own or with Falcon PLM; a person with CRM access
// and no PLM access sees only the CRM menu, and the server refuses every PLM page for them (server/src/app.ts).
// Shared by the server (which enforces it) and the screens.
import { MENU, MENU_FOOTER, type MenuItem } from "./menu.js";
import type { Access } from "./access.js";

/** A person's CRM role. Admins (Full on Settings) and Falcon support work as Sales managers and can change CRM settings. */
export const CRM_ROLES = ["salesperson", "manager"] as const;
export type CrmRole = (typeof CRM_ROLES)[number];
export const CRM_ROLE_LABEL: Record<CrmRole, string> = { salesperson: "Sales person", manager: "Sales manager" };
export const CRM_ROLE_HINT: Record<CrmRole, string> = {
  salesperson: "Sees their own customers, leads and deals (and unassigned leads).",
  manager: "Sees the whole team's customers, leads, deals and calls, and assigns them.",
};

/** The CRM menu. A CRM-only person sees these (and Settings if they have it); someone with PLM too sees them as a
 *  "Falcon CRM" group in the menu. */
export const CRM_MENU: MenuItem[] = [
  { id: "crmtoday", label: "Today", href: "/app/crm" },
  { id: "crmcustomers", label: "Customers", href: "/app/crm/customers" },
  { id: "crmleads", label: "Leads", href: "/app/crm/leads" },
  { id: "crmcontacts", label: "Contacts", href: "/app/crm/contacts" },
  { id: "crmdeals", label: "Deals", href: "/app/crm/deals" },
  { id: "crmtasks", label: "Tasks", href: "/app/crm/tasks" },
];
export const CRM_GROUP: MenuItem = { id: "crmgroup", label: "Falcon CRM", href: "", children: CRM_MENU };
/** Products (Phase 2, the PLM link): Falcon PLM's garments, view only, and new product requests. Shown after Deals when
 *  the organisation has Falcon PLM too and "Link CRM and PLM" is on (Settings → Features). */
export const CRM_PRODUCTS_ITEM: MenuItem = { id: "crmproducts", label: "Products", href: "/app/crm/products" };
/** Quotes and sales orders (Phase 3a, selling): after Deals, for every organisation with Falcon CRM. */
export const CRM_SELLING_ITEMS: MenuItem[] = [
  { id: "crmquotes", label: "Quotes", href: "/app/crm/quotes" },
  { id: "crmorders", label: "Orders", href: "/app/crm/orders" },
];
/** Reports (Phase 3b): sales, pipeline by sales person, best sellers, customers, commission and (Admins) profit. Last. */
export const CRM_REPORTS_ITEM: MenuItem = { id: "crmreports", label: "Reports", href: "/app/crm/reports" };
export const crmMenu = (plmLinked: boolean): MenuItem[] =>
  [...CRM_MENU.slice(0, 5), ...CRM_SELLING_ITEMS, ...(plmLinked ? [CRM_PRODUCTS_ITEM] : []), ...CRM_MENU.slice(5), CRM_REPORTS_ITEM];

/** A new product request: sent to the product team (an R&D development in Falcon PLM), then accepted, a question asked
 *  (the sales person answers it) or declined. Once accepted, the R&D stage shows; once converted, the new garment. */
export const REQUEST_STATES = ["sent", "accepted", "question", "declined"] as const;
export type RequestState = (typeof REQUEST_STATES)[number];
export const REQUEST_STATE_LABEL: Record<RequestState, string> = {
  sent: "Sent · waiting for the product team", accepted: "In R&D", question: "Question from the product team", declined: "Declined",
};

export const ACCOUNT_KINDS = ["customer", "lead"] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
export const LEAD_STATUSES: Array<[string, string]> = [["new", "New"], ["contacted", "Contacted"], ["qualified", "Qualified"], ["lost", "Not interested"]];
export const LEAD_STATUS_LABEL = Object.fromEntries(LEAD_STATUSES) as Record<string, string>;
export const ACTIVITY_KINDS = ["call", "note", "meeting", "email"] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number] | "system";
export const ACTIVITY_LABEL: Record<string, string> = { call: "Call", note: "Note", meeting: "Meeting", email: "Email", system: "Update" };
export const TASK_KINDS: Array<[string, string]> = [["call", "Call"], ["task", "To do"], ["meeting", "Meeting"]];
export const TASK_KIND_LABEL = Object.fromEntries(TASK_KINDS) as Record<string, string>;
/** Call lengths offered after a call (minutes); blank when not known. */
export const CALL_LENGTHS = [1, 2, 3, 5, 10, 15, 20, 30, 45, 60];

/** Deal stages: the organisation's open stages (renamed, added or removed in Settings → Falcon CRM), then Won and Lost. */
export interface Stage { key: string; label: string }
export const WON = "won", LOST = "lost";
export const CLOSED_STAGES: Stage[] = [{ key: WON, label: "Won" }, { key: LOST, label: "Lost" }];

export interface CrmSettings {
  /** Open deal stages, in order (Won and Lost always follow). */
  stages: Stage[];
  /** What can happen on a call (chosen in "How did the call go?"). */
  outcomes: string[];
  /** Where leads come from. */
  sources: string[];
  /** Reasons a deal is lost. */
  lostReasons: string[];
  /** Sales people see every customer, lead and deal (not just their own). */
  shareAll: boolean;
  /** Quotes and orders (Phase 3a): the defaults a new quote starts with and the emails sent to customers. */
  selling: SellingSettings;
  /** Commission (Phase 3b): worked out on profit (selling price minus the approved cost) or on the sales value. The
   *  rates themselves are kept with their dates (crm_commission_rates). Sales managers and Admins change it. */
  commission: CommissionSettings;
}
export const COMMISSION_BASES = ["profit", "sales"] as const;
export type CommissionBasis = (typeof COMMISSION_BASES)[number];
export const COMMISSION_BASIS_LABEL: Record<CommissionBasis, string> = { profit: "Profit (selling price minus the approved cost)", sales: "Sales value (what the customer pays)" };
export interface CommissionSettings { basis: CommissionBasis }
export interface EmailTemplate { subject: string; body: string }
export interface SellingSettings {
  /** Days a new quote is valid for. */
  quoteValidDays: number;
  paymentTerms: string;
  deliveryTerms: string;
  /** The biggest discount (%) a Sales person may give without a Sales manager's OK, for quotes not on a price list
   *  (each price list has its own). */
  discountLimit: number;
  /** Falcon numbers invoices INV-0001…; the number can always be typed instead (from the accounts system). */
  invoicePrefix: string;
  /** What a customer email says. {contact}, {customer}, {company}, {number}, {validUntil}, {total} and {me} are filled in. */
  emails: { quote: EmailTemplate; order: EmailTemplate; priceList: EmailTemplate; datasheet: EmailTemplate };
}
export const DEFAULT_CRM_SETTINGS: CrmSettings = {
  stages: [{ key: "new", label: "New" }, { key: "quoted", label: "Quoted" }, { key: "negotiating", label: "Negotiating" }],
  outcomes: ["Interested", "Call back", "No answer", "Left message", "Not interested", "Order placed"],
  sources: ["Website", "Trade show", "Referral", "Phone enquiry", "Email", "Other"],
  lostReasons: ["Price", "Delivery time", "Went with a competitor", "No budget", "No reply", "Other"],
  shareAll: false,
  commission: { basis: "profit" },
  selling: {
    quoteValidDays: 30, paymentTerms: "30 days from invoice", deliveryTerms: "", discountLimit: 10, invoicePrefix: "INV-",
    emails: {
      quote: { subject: "Quote {number} from {company}", body: "Dear {contact},\n\nThank you for your enquiry. Please find our quote {number} attached ({total}), valid until {validUntil}.\n\nKind regards,\n{me}\n{company}" },
      order: { subject: "Order confirmation {number} from {company}", body: "Dear {contact},\n\nThank you for your order. Please find the order confirmation {number} attached ({total}).\n\nKind regards,\n{me}\n{company}" },
      priceList: { subject: "Our price list from {company}", body: "Dear {contact},\n\nPlease find our current price list attached.\n\nKind regards,\n{me}\n{company}" },
      datasheet: { subject: "Product datasheet from {company}", body: "Dear {contact},\n\nPlease find the product datasheet attached.\n\nKind regards,\n{me}\n{company}" },
    },
  },
};
export const EMAIL_TEMPLATE_KEYS = ["quote", "order", "priceList", "datasheet"] as const;
export type EmailTemplateKey = (typeof EMAIL_TEMPLATE_KEYS)[number];
export const EMAIL_TEMPLATE_LABEL: Record<EmailTemplateKey, string> = { quote: "Quote", order: "Order confirmation", priceList: "Price list", datasheet: "Datasheet" };
/** Fill a template's {words}; unknown ones are left as they are. */
export const fillTemplate = (t: string, v: Record<string, string>) => t.replace(/\{(\w+)\}/g, (m, k) => (k in v ? v[k] : m));
function normalizeSelling(raw: unknown): SellingSettings {
  const r = (raw && typeof raw === "object" ? raw : {}) as any;
  const d = DEFAULT_CRM_SETTINGS.selling;
  const n = (v: unknown, min: number, max: number, f: number) => { const x = Number(v); return Number.isFinite(x) && v !== "" && v !== null ? Math.min(max, Math.max(min, x)) : f; };
  const str = (v: unknown, max: number, f: string) => (typeof v === "string" ? v.trim().slice(0, max) : f);
  const tpl = (v: any, f: EmailTemplate): EmailTemplate => ({ subject: str(v?.subject, 200, "") || f.subject, body: str(v?.body, 4000, "") || f.body });
  return {
    quoteValidDays: Math.round(n(r.quoteValidDays, 1, 365, d.quoteValidDays)), paymentTerms: str(r.paymentTerms, 200, d.paymentTerms), deliveryTerms: str(r.deliveryTerms, 200, d.deliveryTerms),
    discountLimit: n(r.discountLimit, 0, 100, d.discountLimit), invoicePrefix: str(r.invoicePrefix, 12, d.invoicePrefix).replace(/[^\w\-/]/g, "") || d.invoicePrefix,
    emails: { quote: tpl(r.emails?.quote, d.emails.quote), order: tpl(r.emails?.order, d.emails.order), priceList: tpl(r.emails?.priceList, d.emails.priceList), datasheet: tpl(r.emails?.datasheet, d.emails.datasheet) },
  };
}

/** Quotes: Draft → Waiting for OK (a discount over the limit) → OK'd → Sent → Accepted (a sales order) or Declined. A sent
 *  quote past its valid-until date shows as Expired. */
export const QUOTE_STATUSES = ["draft", "approval", "approved", "sent", "accepted", "declined", "expired"] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];
export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = { draft: "Draft", approval: "Waiting for OK", approved: "OK'd · ready to send", sent: "Sent", accepted: "Accepted", declined: "Declined", expired: "Expired" };
/** Sales orders: Confirmed → Invoiced → Part paid → Paid, and Delivered; or Cancelled. */
export const ORDER_STEPS = ["confirmed", "invoiced", "partpaid", "paid", "delivered"] as const;
export type OrderStatus = (typeof ORDER_STEPS)[number] | "cancelled";
export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = { confirmed: "Confirmed", invoiced: "Invoiced", partpaid: "Part paid", paid: "Paid", delivered: "Delivered", cancelled: "Cancelled" };
export const PAYMENT_METHODS = ["Bank transfer", "Card", "Cash", "Cheque", "Letter of credit", "Other"];
export const quoteNo = (n: number) => `Q-${String(n).padStart(4, "0")}`;
export const orderNo = (n: number) => `SO-${String(n).padStart(4, "0")}`;
/** Money rounded to cents (quotes and orders). */
export const cents = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
/** The price each after a discount (to 4 decimal places, like the PLM's prices). */
export const discounted = (list: number, pct: number) => Math.round((Number(list) || 0) * (1 - (Number(pct) || 0) / 100) * 10000) / 10000;
export const MAX_STAGES = 8;
const words = (v: unknown, max: number, fallback: string[]) => {
  const out = Array.isArray(v) ? [...new Set(v.map((x) => String(x ?? "").trim().slice(0, 60)).filter(Boolean))].slice(0, max) : [];
  return out.length ? out : [...fallback];
};
/** Stage keys are kept when a stage is renamed (deals stay in it); a new stage gets a key from its name. */
export const stageKey = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "stage";
export function normalizeCrmSettings(raw: unknown): CrmSettings {
  const r = (raw && typeof raw === "object" ? raw : {}) as any;
  const seen = new Set<string>([WON, LOST]);
  const stages: Stage[] = [];
  for (const s of Array.isArray(r.stages) ? r.stages : []) {
    const label = String(s?.label ?? "").trim().slice(0, 40);
    if (!label) continue;
    let key = String(s?.key ?? "").trim() || stageKey(label);
    if (!/^[a-z0-9-]{1,30}$/.test(key)) key = stageKey(label);
    while (seen.has(key)) key = `${key.slice(0, 27)}-${seen.size}`;
    seen.add(key);
    stages.push({ key, label });
    if (stages.length >= MAX_STAGES) break;
  }
  return {
    stages: stages.length ? stages : DEFAULT_CRM_SETTINGS.stages.map((s) => ({ ...s })),
    outcomes: words(r.outcomes, 12, DEFAULT_CRM_SETTINGS.outcomes),
    sources: words(r.sources, 20, DEFAULT_CRM_SETTINGS.sources),
    lostReasons: words(r.lostReasons, 12, DEFAULT_CRM_SETTINGS.lostReasons),
    shareAll: r.shareAll === true,
    selling: normalizeSelling(r.selling),
    commission: { basis: r.commission?.basis === "sales" ? "sales" : "profit" },
  };
}
export const allStages = (s: CrmSettings): Stage[] => [...s.stages, ...CLOSED_STAGES];
export const stageLabel = (s: CrmSettings, key: string) => allStages(s).find((x) => x.key === key)?.label || key;
export const isOpenStage = (key: string) => key !== WON && key !== LOST;

/** Every PLM menu row: a person with CRM access and no PLM access has none of them (Settings stays for CRM Admins, who
 *  look after users and the CRM settings). */
export const PLM_ROWS = (): string[] => [...MENU, ...MENU_FOOTER].flatMap((it) => it.children || [it]).filter((it) => !it.sameAs && it.id !== "settings").map((it) => it.id)
  .concat(["qcstandard"]);
export function withoutPlm(a: Access): Access {
  const items = { ...a.items };
  for (const r of PLM_ROWS()) items[r] = "none";
  return { ...a, items, allFull: false, except: {}, limited: undefined };
}

/** Import (Customers or Leads): the columns Falcon can read from a spreadsheet. */
export const CRM_IMPORT_FIELDS: Array<{ key: string; label: string; kind: "text"; required?: boolean; aliases?: string[] }> = [
  { key: "name", label: "Company name", kind: "text", required: true, aliases: ["company", "customer", "account", "organisation", "organization", "business", "name"] },
  { key: "contactName", label: "Contact name", kind: "text", aliases: ["contact", "person", "fullname", "contactperson"] },
  { key: "jobTitle", label: "Job title", kind: "text", aliases: ["title", "position", "role"] },
  { key: "phone", label: "Phone", kind: "text", aliases: ["telephone", "tel", "phonenumber", "landline"] },
  { key: "mobile", label: "Mobile", kind: "text", aliases: ["cell", "mobilephone", "cellphone"] },
  { key: "email", label: "Email", kind: "text", aliases: ["emailaddress", "mail"] },
  { key: "website", label: "Website", kind: "text", aliases: ["web", "url", "site"] },
  { key: "address", label: "Address", kind: "text", aliases: ["street", "address1", "addressline1"] },
  { key: "city", label: "Town / city", kind: "text", aliases: ["town", "city"] },
  { key: "postcode", label: "Postcode", kind: "text", aliases: ["zip", "zipcode", "postalcode"] },
  { key: "country", label: "Country", kind: "text" },
  { key: "source", label: "Lead source", kind: "text", aliases: ["source", "leadsource", "origin"] },
  { key: "tags", label: "Tags", kind: "text", aliases: ["tag", "group", "sector", "segment"] },
  { key: "notes", label: "Notes", kind: "text", aliases: ["note", "comments", "description"] },
];
export const CRM_IMPORT_MAX = 2000;

/** A phone number as a tel: link (digits and a leading +), or "" when there is nothing to dial. */
export function telHref(phone: string | null | undefined): string {
  const p = String(phone || "").trim();
  const digits = p.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return digits.replace(/\D/g, "").length >= 3 ? `tel:${digits}` : "";
}
/** A WhatsApp chat link for a mobile number in international form (+44…); "" when the number has no country code. */
export function whatsAppHref(phone: string | null | undefined): string {
  const p = String(phone || "").trim();
  if (!p.startsWith("+") && !p.startsWith("00")) return "";
  const digits = p.replace(/^00/, "").replace(/\D/g, "");
  return digits.length >= 8 ? `https://wa.me/${digits}` : "";
}
export const fullContactName = (c: { firstName?: string; lastName?: string } | null | undefined) => [c?.firstName, c?.lastName].filter(Boolean).join(" ");

/** Report dates (Phase 3b): the last 7 days, this month, last month, the last 3 months, or the person's own From and To. */
export const REPORT_RANGES = ["7days", "month", "lastmonth", "3months", "custom"] as const;
export type ReportRange = (typeof REPORT_RANGES)[number];
export const REPORT_RANGE_LABEL: Record<ReportRange, string> = { "7days": "Last 7 days", month: "This month", lastmonth: "Last month", "3months": "Last 3 months", custom: "Custom dates" };
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
/** The first and last day of a report range, counted from today (yyyy-mm-dd, both days included). */
export function reportDates(range: string, today: string, from?: string | null, to?: string | null): { range: ReportRange; from: string; to: string } {
  const t = new Date(`${today}T12:00:00Z`);
  const y = t.getUTCFullYear(), m = t.getUTCMonth();
  const day = (yy: number, mm: number, dd: number) => isoDay(new Date(Date.UTC(yy, mm, dd, 12)));
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (range === "custom" && from && to && iso.test(from) && iso.test(to)) return from <= to ? { range: "custom", from, to } : { range: "custom", from: to, to: from };
  if (range === "7days") return { range, from: day(y, m, t.getUTCDate() - 6), to: today };
  // This year (1 January to today) and last year (the whole year): the CRM Reports section's sales reports.
  if (range === "year") return { range: range as ReportRange, from: `${y}-01-01`, to: today };
  if (range === "lastyear") return { range: range as ReportRange, from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  if (range === "lastmonth") return { range, from: day(y, m - 1, 1), to: day(y, m, 0) };
  if (range === "3months") {
    // The same day three months ago (the last day of that month when it is shorter), up to today.
    const last = new Date(Date.UTC(y, m - 2, 0, 12)).getUTCDate();
    return { range, from: day(y, m - 3, Math.min(t.getUTCDate(), last) + 1), to: today };
  }
  return { range: "month", from: day(y, m, 1), to: today };
}
